import Config from 'config';
import store from '../store';
import { setStepperFromJSApi } from '../store/JSApiSlice';
import IProperty from './constants/IProperty';
import ApiaForm from './ApiaForm';
import {
  IApiaFunctions,
  IApiaField,
  IApiaForm,
  IIProperty,
  TOpenModal,
} from './types';
import { buttonsActions } from '../store/buttonsSlice';
import ActionButton from './ActionButton';
import { AdminEntityController } from '../components/apia/execution/AdminEntity';
import { notify } from '../components/common/notifications/util';
import { apiaFunctionsModalEmitter } from '../components/common/dialogs/modals/ApiaFunctionsModals';
import { EXECUTION_FORM } from '../components/apia/execution/FormsController';
import ApiaApi from '../api/apiaApi/apiaApi';
import { ApiaUtil, TDispatchedNotification } from '@apia/components';
import { arrayOrArray } from '@apia/util';

declare global {
  interface Window {
    lastModalReturn: string[];
  }
}

const OptionButton = {
  BTN_VIEW_DOCS: 100,

  BTN_PRINT: 101,
};

export default class ApiaFunctions implements IApiaFunctions {
  iProperty: IIProperty;

  #frmArr: IApiaForm[];

  #store;

  constructor() {
    this.#frmArr = [];
    this.#store = store;
    this.iProperty = IProperty;
  }

  getFormByType(frmName: string, frmType: string): IApiaForm | null {
    const forms = document.querySelectorAll(`[data-form-type=${frmType} i]`);
    for (let i = 0; i < forms.length; i += 1) {
      if (
        forms[i].getAttribute('data-form-name')?.toUpperCase() ===
        frmName.toUpperCase()
      ) {
        // find existing instance of IApiaForm or return new one if it doesnt
        // exist
        const f = this.#frmArr.findIndex(
          (item: IApiaForm) => item.frmId === forms[i].getAttribute('id'),
        );
        if (f > -1) {
          return this.#frmArr[f];
        }
        const form: ApiaForm = new ApiaForm(forms[i] as HTMLElement);
        this.#frmArr.push(form);
        return form;
      }
    }
    return null;
  }

  actionButton(id: string | number) {
    const actualId = typeof id === 'number' ? this.getButtonAction(id) : id;
    return new ActionButton(actualId);
  }

  admEntity(
    entName: string,
    entNum?: number,
    parameters?: string,
    width?: number,
    height?: number,
  ) {
    AdminEntityController.emit('adminEntity', {
      entName,
      entNum,
      parameters,
      width,
      height,
    });
  }

  changeTab(index: number | number[]) {
    window.TabsController.openTabs(arrayOrArray(index));
  }

  getTabNumber() {
    return window.TabsController.tabsArray
      .map((c, i) => ({ c, i }))
      .filter((c) => c.c.open)
      .map((c) => c.i);
  }

  getTabTitle(index: number) {
    return window.TabsController.tabsArray[index].label;
  }

  clearMessages() {
    ApiaUtil.instance.notifications.closeAll();
  }

  clickActionButton(button: number) {
    const btn = Number(button);
    let btnAction;

    switch (btn) {
      case ActionButton.BTN_CONFIRM:
        btnAction = 'confirm';
        break;
      case ActionButton.BTN_NEXT:
        btnAction = 'next';
        break;
      case ActionButton.BTN_PREV:
        btnAction = 'previous';
        break;
      case ActionButton.BTN_SAVE:
        btnAction = 'save';
        break;
      case ActionButton.BTN_RELEASE:
        btnAction = 'release';
        break;
      case ActionButton.BTN_DELEGATE:
        btnAction = 'delegate';
        break;
      case ActionButton.BTN_SHARE:
        btnAction = 'socialShare';
        break;
      case OptionButton.BTN_VIEW_DOCS:
        btnAction = 'viewDocs';
        break;
      case OptionButton.BTN_PRINT:
        btnAction = 'print';
        break;
      case ActionButton.BTN_CLOSE:
        btnAction = 'close';
        break;
      default:
        return;
    }

    (
      document.querySelector(
        `[data-action$="${btnAction}"]`,
      ) as HTMLButtonElement
    )?.click();
  }

  closeCurrentTab(): void {
    ApiaUtil.instance.tabs.currentTab.close();
  }

  disableActionButton(id: string | number): void {
    const actualId = typeof id === 'number' ? this.getButtonAction(id) : id;
    this.#store.dispatch(
      buttonsActions.updateButton({ id: actualId, disabled: true }),
    );
  }

  disableOptionButton(id: string | number): void {
    const actualId = typeof id === 'number' ? this.getButtonAction(id) : id;
    this.disableActionButton(actualId);
  }

  enableActionButton(id: string | number): void {
    const actualId = typeof id === 'number' ? this.getButtonAction(id) : id;
    this.#store.dispatch(
      buttonsActions.updateButton({ id: actualId, disabled: false }),
    );
  }

  enableOptionButton(id: string | number): void {
    const actualId = typeof id === 'number' ? this.getButtonAction(id) : id;
    this.enableActionButton(actualId);
  }

  getAllForms(): IApiaForm[] {
    const entityForms = document.querySelectorAll("[data-form-type='E' i]");
    for (let i = 0; i < entityForms.length; i += 1) {
      const frmNameE = entityForms[i].getAttribute('data-form-name');
      if (frmNameE !== null) this.getFormByType(frmNameE, 'E');
    }
    const processForms = document.querySelectorAll("[data-form-type='P' i]");
    for (let i = 0; i < processForms.length; i += 1) {
      const frmNameP = processForms[i].getAttribute('data-form-name');
      if (frmNameP) this.getFormByType(frmNameP, 'P');
    }
    return this.#frmArr;
  }

  getAttField(
    attType: string,
    attId: string,
  ): IApiaField | IApiaField[] | null {
    if (attType && attId) {
      let field;
      const forms = document.querySelectorAll(`[data-form-type=${attType} i]`);
      for (let i = 0; i < forms.length; i += 1) {
        const f = forms[i].getAttribute('data-form-name');
        if (f) {
          const form = this.getFormByType(f, attType);
          if (form) {
            field = form.getAttField(attId);
            if (field !== null) return field;
          }
        }
      }
    }
    return null;
  }

  getButtonAction(id: number) {
    let btnId: string;

    switch (id) {
      case ActionButton.BTN_CLOSE:
        btnId = 'btnClose';
        break;
      case ActionButton.BTN_CONFIRM:
        btnId = 'btnCon';
        break;
      case ActionButton.BTN_DELEGATE:
        btnId = 'lblExeDelegar';
        break;
      case ActionButton.BTN_NEXT:
        btnId = 'btnSig';
        break;
      case ActionButton.BTN_PREV:
        btnId = 'btnAnt';
        break;
      case ActionButton.BTN_PRINT:
        btnId = 'btnStaPri';
        break;
      case ActionButton.BTN_RELEASE:
        btnId = 'btnEjeLib';
        break;
      case ActionButton.BTN_SAVE:
        btnId = 'btnGua';
        break;
      case ActionButton.BTN_SIGN:
        btnId = 'btnSign';
        break;
      case ActionButton.BTN_SHARE:
        btnId = 'lblShareMsg';
        break;
      case ActionButton.BTN_VIEW_DOCS:
        btnId = 'sbtEjeDoc';
        break;
      default:
        throw new Error(`There is no button with id ${id}`);
    }
    return btnId;
  }

  getCurrentLanguageCode(): string | undefined {
    return Config.LANG_CODE;
  }

  getCurrentProcessName(): string | undefined {
    return Config.CURRENT_PROCESS_NAME;
  }

  getCurrentStep(): number | undefined {
    return Config.CURRENT_STEP;
  }

  getCurrentTaskName(): string | undefined {
    return Config.CURRENT_TASK_NAME;
  }

  getEntityForm(frmName: string): IApiaForm | null {
    const frm = this.getForm(frmName, 'E');
    if (frm) return frm;
    return null;
  }

  getForm(frmName: string, frmType?: string): IApiaForm | null {
    if (frmType === 'E') return this.getFormByType(frmName, frmType);
    if (frmType === 'P') return this.getFormByType(frmName, frmType);
    const eFrm = this.getFormByType(frmName, 'E');
    if (eFrm) {
      return eFrm;
    }
    const pForm = this.getFormByType(frmName, 'P');
    return pForm;
  }

  getLastModalReturn() {
    return window.lastModalReturn;
  }

  getModalReturn() {
    return this.getLastModalReturn();
  }

  getModalSelectedRow() {
    return window.lastModalReturn.slice(2);
  }

  getModalShowValue() {
    return window.lastModalReturn[1];
  }

  getModalValue() {
    return window.lastModalReturn[0];
  }

  getProcessForm(frmName: string): IApiaForm | null {
    const frm = this.getForm(frmName, 'P');
    if (frm) return frm;
    return null;
  }

  getRootPath(): string | null {
    try {
      return Config.APIA_ROOT_CONTEXT;
    } catch (e: unknown) {
      if (e instanceof Error) {
        console.error(`Apia JSApi Error: ${e.message}`);
      }
    }
    return null;
  }

  hideActionButton(id: string | number): void {
    const actualId = typeof id === 'number' ? this.getButtonAction(id) : id;
    this.#store.dispatch(
      buttonsActions.updateButton({ id: actualId, hidden: true }),
    );
  }

  hideOptionButton(id: string | number): void {
    const actualId = typeof id === 'number' ? this.getButtonAction(id) : id;
    this.hideActionButton(actualId);
  }

  openModal(props: TOpenModal) {
    apiaFunctionsModalEmitter.emit('open', props);
  }

  openTab(title: string, url: string): void {
    void ApiaUtil.instance.tabs.openTab(title, url);
  }

  saveTask() {
    const { submitValues: values } =
      store.getState().previousValidationSlice.forms[EXECUTION_FORM];
    void ApiaApi.post(Config.SAVE_TASK_URL(), {
      postData: values,
      postDataTreatement: 'stringify',
    });
  }

  setLastModalReturn(value: string[]) {
    window.lastModalReturn = value;
    window.lastModalReturn = value;
  }

  setStepTitle(stepNumber: number, stepTitle: string): void {
    this.#store.dispatch(setStepperFromJSApi({ stepNumber, stepTitle }));
  }

  showActionButton(id: string | number): void {
    const actualId = typeof id === 'number' ? this.getButtonAction(id) : id;
    this.#store.dispatch(
      buttonsActions.updateButton({ id: actualId, hidden: false }),
    );
  }

  showConfirm(
    text: string,
    title: string,
    callbackFn: (win: boolean) => unknown,
  ): void {
    void ApiaUtil.instance.dialogs
      .confirm({ children: text, title })
      .then((hasConfirmed) => {
        try {
          callbackFn(hasConfirmed);
        } catch (e) {
          if (e instanceof Error) {
            notify({ message: String(e), type: 'danger' });
          }
        }
      });
  }

  showMessage(
    text: string,
    title?: string,
    type?: TDispatchedNotification['type'],
  ): void {
    notify({ message: text, type: type ?? 'warning', title });
  }

  showOptionButton(id: string | number): void {
    const actualId = typeof id === 'number' ? this.getButtonAction(id) : id;
    this.showActionButton(actualId);
  }

  // converts a number in an Apia formatted number
  toApiaNumber(value: number): number | string {
    return ApiaUtil.instance.parsers.apiaNumber(value);
  }

  // converts an Apia formatted number into a javascript number
  toJSNumber(value: string): number | string {
    return ApiaUtil.instance.parsers.jsNumber(value);
  }

  viewAdmEntity(entName: string, entNum?: number) {
    AdminEntityController.emit('adminEntity', {
      entName,
      entNum,
      viewMode: true,
    });
  }
}
